<template>
  <div class="time-section">
    <span v-if="isMoreThanOneDay" class="time-section-text">{{ daysText }}</span>
    <div v-else class="time-section-time">
      <span class="time-section-time-num">{{ formattedHours }}</span>
      :
      <span class="time-section-time-num">{{ formattedMinutes }}</span>
      :
      <span class="time-section-time-num">{{ formattedSeconds }}</span>
      后失效
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, onUnmounted, watch, defineEmits } from 'vue'
  import type { PropType } from 'vue'

  const emit = defineEmits(['timeEnd'])

  defineOptions({
    name: 'AppTicketTime'
  })

  const props = defineProps({
    time: {
      type: [Number, String, Date] as PropType<number | string | Date | undefined>,
      default: undefined
    }
  })

  const remainingSeconds = ref(0)
  let timer: number | null = null

  const hours = computed(() => Math.floor(remainingSeconds.value / 3600))
  const minutes = computed(() => Math.floor((remainingSeconds.value % 3600) / 60))
  const seconds = computed(() => remainingSeconds.value % 60)

  const formattedHours = computed(() => String(hours.value).padStart(2, '0'))
  const formattedMinutes = computed(() => String(minutes.value).padStart(2, '0'))
  const formattedSeconds = computed(() => String(seconds.value).padStart(2, '0'))

  const isMoreThanOneDay = computed(() => remainingSeconds.value > 86400)

  // 计算天数：超过48小时按照2天计算，24-48小时按照1天计算
  const days = computed(() => Math.floor(remainingSeconds.value / 86400))
  const daysText = computed(() => `${days.value}天后失效`)

  const parseTimeToSeconds = (value?: number | string | Date) => {
    if (value == null) {
      return 0
    }

    if (typeof value === 'number') {
      if (!Number.isFinite(value) || value <= 0) {
        return 0
      }

      if (value > 1e11) {
        return Math.max(0, Math.floor((value - Date.now()) / 1000))
      }

      if (value > 1e9) {
        const currentSeconds = Math.floor(Date.now() / 1000)
        return Math.max(0, Math.floor(value - currentSeconds))
      }

      return Math.floor(value)
    }

    const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime()

    if (Number.isNaN(timestamp)) {
      return 0
    }

    return Math.max(0, Math.floor((timestamp - Date.now()) / 1000))
  }

  const restartCountdown = (value?: number | string | Date) => {
    stopCountdown()
    remainingSeconds.value = parseTimeToSeconds(value)
    if (remainingSeconds.value > 0) {
      startCountdown()
    }
  }

  const startCountdown = () => {
    if (timer !== null) {
      return
    }
    timer = window.setInterval(() => {
      if (remainingSeconds.value > 0) {
        remainingSeconds.value--
      } else {
        emit('timeEnd')
        stopCountdown()
      }
    }, 1000)
  }

  const stopCountdown = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  watch(
    () => [props.time] as const,
    ([time]) => {
      restartCountdown(time)
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopCountdown()
  })
</script>

<style lang="stylus" scoped>
  @import './index.styl';
</style>
